import React, { useEffect, useMemo, useState } from "react";
import {
  HISTORY_INTERVAL_MS,
  RANGE_MILLISECONDS,
  buildServiceDisruptionSegments,
  buildStatusSegments,
  calculateAvailability,
  countIncidents,
  downsampleHistory,
  normalizeHistoryPayload,
  reasonLabel,
  samplesForRange,
} from "../feedHealthHistory";
import "./DataHealthPage.css";

const HISTORY_URL = "https://caltrain.live/feed-health-history.v1.json";
const CHART_WIDTH = 900;
const CHART_HEIGHT = 220;
const CHART_MARGIN = { top: 18, right: 18, bottom: 34, left: 46 };

const statusLabel = (status) => ({
  healthy: "Healthy",
  degraded: "Degraded",
  unavailable: "Unavailable",
  recovering: "Recovering",
  idle: "No scheduled service",
}[status] || "Unknown");

const disruptionLabel = (serviceDisruption) => {
  if (!serviceDisruption) return "Official alert status unknown";
  if (!serviceDisruption.active) return "No matching official disruption alert";
  const labels = {
    cancellation: "Cancellation",
    delay: "Delay",
    emergency: "Emergency",
  };
  const categories = serviceDisruption.categories
    .map((category) => labels[category] || category)
    .join(", ");
  const alertCount = `${serviceDisruption.alertCount} alert${
    serviceDisruption.alertCount === 1 ? "" : "s"
  }`;
  const affected = serviceDisruption.affectedTrains.includes("ALL")
    ? "all trains"
    : serviceDisruption.affectedTrains.length > 0
      ? `train${serviceDisruption.affectedTrains.length === 1 ? "" : "s"} ${serviceDisruption.affectedTrains.join(", ")}`
      : null;
  return [categories, alertCount, affected].filter(Boolean).join(" · ");
};

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "America/Los_Angeles",
  timeZoneName: "short",
});

const shortTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "America/Los_Angeles",
});

const splitIntoContinuousRuns = (samples, intervalMs) => {
  const runs = [];
  (samples || []).forEach((sample) => {
    const run = runs[runs.length - 1];
    const previous = run?.[run.length - 1];
    if (!previous || sample.timestamp - previous.timestamp > intervalMs * 1.5) {
      runs.push([sample]);
    } else {
      run.push(sample);
    }
  });
  return runs;
};

const linePath = ({ samples, key, start, end, maximum }) => {
  const innerWidth = CHART_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right;
  const innerHeight = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;
  const x = (timestamp) =>
    CHART_MARGIN.left + ((timestamp - start) / Math.max(1, end - start)) * innerWidth;
  const y = (value) =>
    CHART_MARGIN.top + innerHeight - (value / Math.max(1, maximum)) * innerHeight;

  return splitIntoContinuousRuns(samples, HISTORY_INTERVAL_MS)
    .map((run) =>
      downsampleHistory(run)
        .map((sample, index) =>
          `${index === 0 ? "M" : "L"}${x(sample.timestamp).toFixed(2)},${y(sample[key]).toFixed(2)}`,
        )
        .join(" "),
    )
    .join(" ");
};

const nearestSample = (samples, targetTimestamp) => {
  if (!samples.length) return null;
  let low = 0;
  let high = samples.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (samples[middle].timestamp < targetTimestamp) low = middle + 1;
    else high = middle;
  }
  const candidate = samples[low];
  const prior = samples[Math.max(0, low - 1)];
  return Math.abs(prior.timestamp - targetTimestamp) <=
    Math.abs(candidate.timestamp - targetTimestamp)
    ? prior
    : candidate;
};

const HistoryLineChart = ({
  title,
  samples,
  start,
  end,
  expectedKey,
  observedKey,
  expectedLabel,
  observedLabel,
}) => {
  const [hovered, setHovered] = useState(null);
  const maximum = Math.max(
    1,
    ...samples.flatMap((sample) => [sample[expectedKey], sample[observedKey]]),
  );
  const expectedPath = linePath({
    samples,
    key: expectedKey,
    start,
    end,
    maximum,
  });
  const observedPath = linePath({
    samples,
    key: observedKey,
    start,
    end,
    maximum,
  });
  const innerWidth = CHART_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right;
  const innerHeight = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;
  const hoverX = hovered
    ? CHART_MARGIN.left + ((hovered.timestamp - start) / Math.max(1, end - start)) * innerWidth
    : null;
  const hoverY = (key) =>
    CHART_MARGIN.top + innerHeight - (hovered[key] / maximum) * innerHeight;

  const handlePointer = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
    setHovered(nearestSample(samples, start + ratio * (end - start)));
  };

  return (
    <section className="health-chart" aria-label={title}>
      <div className="health-chart-heading">
        <h3>{title}</h3>
        <div className="health-line-legend" aria-label={`${title} legend`}>
          <span><i className="expected-line-key" />{expectedLabel}</span>
          <span><i className="observed-line-key" />{observedLabel}</span>
        </div>
      </div>
      <div className="health-chart-stage">
        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          role="img"
          aria-label={`${title} over the selected time range`}
          onPointerMove={handlePointer}
          onPointerLeave={() => setHovered(null)}
        >
          <line className="health-axis" x1={CHART_MARGIN.left} y1={CHART_MARGIN.top} x2={CHART_MARGIN.left} y2={CHART_HEIGHT - CHART_MARGIN.bottom} />
          <line className="health-axis" x1={CHART_MARGIN.left} y1={CHART_HEIGHT - CHART_MARGIN.bottom} x2={CHART_WIDTH - CHART_MARGIN.right} y2={CHART_HEIGHT - CHART_MARGIN.bottom} />
          <line className="health-gridline" x1={CHART_MARGIN.left} y1={CHART_MARGIN.top + innerHeight / 2} x2={CHART_WIDTH - CHART_MARGIN.right} y2={CHART_MARGIN.top + innerHeight / 2} />
          <text className="health-axis-label" x={CHART_MARGIN.left - 10} y={CHART_MARGIN.top + 4} textAnchor="end">{maximum}</text>
          <text className="health-axis-label" x={CHART_MARGIN.left - 10} y={CHART_MARGIN.top + innerHeight / 2 + 4} textAnchor="end">{Math.round(maximum / 2)}</text>
          <text className="health-axis-label" x={CHART_MARGIN.left - 10} y={CHART_HEIGHT - CHART_MARGIN.bottom + 4} textAnchor="end">0</text>
          <text className="health-axis-label" x={CHART_MARGIN.left} y={CHART_HEIGHT - 9}>{shortTimeFormatter.format(new Date(start))}</text>
          <text className="health-axis-label" x={CHART_WIDTH - CHART_MARGIN.right} y={CHART_HEIGHT - 9} textAnchor="end">{shortTimeFormatter.format(new Date(end))}</text>
          <path className="health-expected-line" d={expectedPath} />
          <path className="health-observed-line" d={observedPath} />
          {hovered && (
            <>
              <line className="health-hover-line" x1={hoverX} y1={CHART_MARGIN.top} x2={hoverX} y2={CHART_HEIGHT - CHART_MARGIN.bottom} />
              <circle className="health-hover-expected" cx={hoverX} cy={hoverY(expectedKey)} r="5" />
              <circle className="health-hover-observed" cx={hoverX} cy={hoverY(observedKey)} r="5" />
            </>
          )}
        </svg>
        {hovered && (
          <div className="health-chart-tooltip" role="status">
            <strong>{dateTimeFormatter.format(new Date(hovered.timestamp))}</strong>
            <span>{expectedLabel}: {hovered[expectedKey]}</span>
            <span>{observedLabel}: {hovered[observedKey]}</span>
            <span>Status: {statusLabel(hovered.status)}</span>
            <span>Official alerts: {disruptionLabel(hovered.serviceDisruption)}</span>
          </div>
        )}
      </div>
    </section>
  );
};

const DataHealthPage = ({ feedHealth, historyUrl = HISTORY_URL }) => {
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("24h");
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await fetch(historyUrl, { cache: "no-store" });
        if (!response.ok) throw new Error(`History request failed (${response.status})`);
        const normalized = normalizeHistoryPayload(await response.json());
        if (active) {
          setHistory(normalized);
          setNow(new Date());
          setError(null);
        }
      } catch (loadError) {
        if (active) setError(loadError);
      }
    };
    load();
    const interval = setInterval(load, HISTORY_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [historyUrl]);

  const samples = useMemo(
    () => samplesForRange(history?.samples || [], range, now),
    [history, range, now],
  );
  const rangeMs = RANGE_MILLISECONDS[range];
  const rangeStart = now.getTime() - rangeMs;
  const rangeEnd = now.getTime();
  const statusSegments = buildStatusSegments(
    samples,
    rangeStart,
    rangeEnd,
    (history?.intervalSeconds || 300) * 1000,
  );
  const disruptionSegments = buildServiceDisruptionSegments(
    samples,
    rangeStart,
    rangeEnd,
    (history?.intervalSeconds || 300) * 1000,
  );
  const availability = calculateAvailability(samples);
  const incidents = countIncidents(samples);
  const latest = samples[samples.length - 1];
  const currentStatus = feedHealth?.status || latest?.status || "unknown";
  const currentReason = feedHealth?.reason || latest?.reason;

  return (
    <main className="data-health-page">
      <header className="data-health-heading">
        <div>
          <p className="eyebrow">Caltrain vehicle data</p>
          <h1>Data status</h1>
        </div>
        <p>History of the location data received and validated by Caltrain Live.</p>
      </header>

      <section className="health-current" aria-labelledby="current-health-heading">
        <div>
          <p className="health-meta-label">Current status</p>
          <h2 id="current-health-heading">
            <span className={`health-status-dot status-${currentStatus}`} aria-hidden="true" />
            {statusLabel(currentStatus)}
          </h2>
          {currentReason && <p>{reasonLabel(currentReason)}</p>}
        </div>
        <dl>
          <div><dt>Observed trains</dt><dd>{latest ? `${latest.observedTrainCount} of ${latest.expectedTrainCount}` : "—"}</dd></div>
          <div><dt>Observed moving</dt><dd>{latest ? `${latest.observedTrainsMoving} of ${latest.expectedTrainsMoving}` : "—"}</dd></div>
          <div><dt>Last history sample</dt><dd>{latest ? dateTimeFormatter.format(new Date(latest.timestamp)) : "—"}</dd></div>
        </dl>
      </section>

      <section className="health-history-section" aria-labelledby="health-history-heading">
        <div className="health-history-heading">
          <div>
            <p className="health-meta-label">History</p>
            <h2 id="health-history-heading">Observed feed health</h2>
          </div>
          <div className="health-range-picker" role="group" aria-label="History range">
            {[['24h', '24 hours'], ['7d', '7 days'], ['30d', '30 days']].map(([value, label]) => (
              <button key={value} type="button" aria-pressed={range === value} onClick={() => setRange(value)}>{label}</button>
            ))}
          </div>
        </div>

        {error && !history && <div className="health-load-message" role="alert">Health history is temporarily unavailable.</div>}
        {!error && !history && <div className="health-load-message" role="status">Loading health history…</div>}

        {history && (
          <>
            <div className="health-summary-row">
              <div><span>Healthy during monitored service</span><strong>{availability === null ? "—" : `${availability.toFixed(1)}%`}</strong></div>
              <div><span>Data incidents</span><strong>{incidents}</strong></div>
              <div><span>Recorded samples</span><strong>{samples.length.toLocaleString()}</strong></div>
            </div>

            <div className="health-status-timeline" aria-label="Feed status timeline">
              {statusSegments.map((segment) => {
                const left = ((segment.start - rangeStart) / rangeMs) * 100;
                const width = ((segment.end - segment.start) / rangeMs) * 100;
                return (
                  <span
                    key={`${segment.start}-${segment.status}`}
                    className={`timeline-${segment.status}`}
                    style={{ left: `${left}%`, width: `${Math.max(width, 0.15)}%` }}
                    title={`${statusLabel(segment.status)}${segment.reason ? `: ${reasonLabel(segment.reason)}` : ""}`}
                  />
                );
              })}
            </div>
            <div className="health-status-legend">
              {['healthy', 'degraded', 'unavailable', 'recovering', 'idle'].map((status) => (
                <span key={status}><i className={`status-${status}`} />{statusLabel(status)}</span>
              ))}
              <span><i className="status-unknown" />No sample</span>
            </div>

            <div className="health-disruption-row">
              <span>Official disruption alerts</span>
              <div className="health-disruption-timeline" aria-label="Official delay, cancellation, or emergency alerts">
                {disruptionSegments.map((segment) => {
                  const left = ((segment.start - rangeStart) / rangeMs) * 100;
                  const width = ((segment.end - segment.start) / rangeMs) * 100;
                  return (
                    <span
                      key={`${segment.start}-${segment.categoryKey}`}
                      style={{ left: `${left}%`, width: `${Math.max(width, 0.15)}%` }}
                      title={`${segment.categories.join(", ")} (${segment.alertCount} alert${segment.alertCount === 1 ? "" : "s"})`}
                    />
                  );
                })}
              </div>
            </div>

            {samples.length > 0 ? (
              <div className="health-charts">
                <HistoryLineChart
                  title="Trains reported"
                  samples={samples}
                  start={rangeStart}
                  end={rangeEnd}
                  expectedKey="expectedTrainCount"
                  observedKey="observedTrainCount"
                  expectedLabel="Expected trains"
                  observedLabel="Observed trains"
                />
                <HistoryLineChart
                  title="Trains moving"
                  samples={samples}
                  start={rangeStart}
                  end={rangeEnd}
                  expectedKey="expectedTrainsMoving"
                  observedKey="observedTrainsMoving"
                  expectedLabel="Expected moving"
                  observedLabel="Observed moving"
                />
              </div>
            ) : (
              <div className="health-load-message">No samples are available for this range yet.</div>
            )}
          </>
        )}
      </section>

      <p className="health-method-note">
        Moving counts exclude trains waiting at their origin or destination. This page reflects Caltrain Live’s observations of data received from Caltrain’s location service partner; it is not an official 511 availability report. Gaps indicate that no history sample was published.
      </p>
    </main>
  );
};

export default DataHealthPage;
